import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { Link } from "react-router-dom";
import Share from "../../assets/icons/share.svg";
import customStyles from "../../customStyles";
import { accountMetrics } from "../../utils/store/reducers/accountMetricsSlice";
import { setDefaultLoginId } from "../../utils/store/reducers/accountSlice";
import {
  accountLoginIdsListReq,
  updateAccountMetrics,
} from "../../utils/apis/accountsApi";
import { Button, message, Modal, Drawer } from "antd";
import { LinkOutlined, StopOutlined, SyncOutlined } from "@ant-design/icons";
import {
  FacebookShareButton,
  FacebookIcon,
  TwitterShareButton,
  XIcon,
  WhatsappShareButton,
  WhatsappIcon,
  TelegramShareButton,
  TelegramIcon,
  EmailShareButton,
  EmailIcon,
} from "react-share";
import "./style.scss";

const AccountMetricsHeader = ({ onResetAccount, showResetButton = false }) => {
  const { login_id } = useSelector((state) => state?.accountList);
  const { data, isLoading, error } = useSelector(
    (state) => state?.accountMetrics
  );
  const { results } = useSelector((state) => state?.accountList?.data);
  const { idToken } = useSelector((state) => state?.auth);
  const dispatch = useDispatch();

  const [accountLoginIdsList, setAccountLoginIdsList] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isPublic, setIsPublic] = useState(data?.is_public || false);
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);

  const statusMapping = {
    0: "Active",
    1: "Locked for day",
    2: "Violation",
    3: "Inactive",
    4: "Closed",
    5: "GoalAchieved",
    6: "Ineligible",
  };
  const colorClassMapping = {
    0: "status-active",
    1: "status-temporary-violation",
    2: "status-violation",
    3: "status-inactive",
    4: "status-closed",
    5: "status-goal-achieved",
    6: "status-ineligible",
  };
  const statusCode = data?.account_metrics?.get_trading_accounts?.status;
  const colorClass = colorClassMapping[statusCode] || "status-unknown";
  const statusText = statusMapping[statusCode] || "Unknown status";

  const activeAccount = accountLoginIdsList?.find((item) => {
    return Number(item.value) === Number(login_id);
  });

  const activeAccountFromList = results?.find(
    (item) => Number(item.login_id) === Number(activeAccount?.value)
  );

  const options = accountLoginIdsList;

  useEffect(() => {
    const fetchAccountLoginIds = async () => {
      try {
        const response = await accountLoginIdsListReq(idToken);
        setAccountLoginIdsList(response);
        if (!login_id) {
          dispatch(setDefaultLoginId(response[0].value));
          dispatch(accountMetrics({ id: response[0].value, idToken: idToken }));
        }
      } catch (error) {
        console.error("Error fetching account login IDs:", error);
      }
    };

    fetchAccountLoginIds();
  }, [idToken]);

  useEffect(() => {
    login_id !== null &&
      dispatch(accountMetrics({ id: login_id, idToken: idToken }));
    dispatch(setDefaultLoginId(login_id));
  }, [dispatch]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 600);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    data && setIsPublic(data?.is_public);
  }, [data]);

  const handleChange = (selectedOption) => {
    Number(selectedOption.value) !== login_id &&
      dispatch(setDefaultLoginId(selectedOption.value));
    dispatch(accountMetrics({ id: selectedOption.value, idToken: idToken }));
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleCopy = () => {
    const certificateUrl = `${window.location.origin}/public-account-stats/${data?.login_id}`;
    navigator.clipboard.writeText(certificateUrl);
    setCopied(true);
    message.success("Link copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const toggleCertificate = async () => {
    try {
      setLoading(true);
      const response = await updateAccountMetrics(data?.login_id, {
        is_public: !isPublic,
        public_permissions: isPublic ? [1] : [],
      });
      setIsPublic(response?.is_public || !isPublic);
      message.success(
        response?.is_public ? "Link generated successfully!" : "Link disabled."
      );
    } catch (error) {
      message.error("Failed to update link status.");
    } finally {
      setLoading(false);
    }
  };

  const ShareCertificateModal = ({ data, isModalOpen, handleClose }) => {
    const certificateUrl = `${window.location.origin}/public-account-stats/${data?.login_id}`;

    const content = (
      <>
        {isPublic ? (
          <>
            <p>Link generated. Copy and share it!</p>
            <p>You can disable the link anytime.</p>
            <Button
              type="primary"
              danger
              icon={<StopOutlined />}
              loading={loading}
              onClick={toggleCertificate}
            >
              Disable Link
            </Button>
          </>
        ) : (
          <div className="generate-link-container">
            <p>Link not generated. Please generate a link to share.</p>
            <Button
              type="primary"
              icon={<SyncOutlined />}
              loading={loading}
              onClick={toggleCertificate}
            >
              Generate Link
            </Button>
          </div>
        )}

        {isPublic && (
          <>
            <div className="share-options">
              <FacebookShareButton url={certificateUrl}>
                <FacebookIcon size={50} round />
              </FacebookShareButton>
              <TwitterShareButton url={certificateUrl}>
                <XIcon size={50} round />
              </TwitterShareButton>
              <WhatsappShareButton url={certificateUrl}>
                <WhatsappIcon size={50} round />
              </WhatsappShareButton>
              <TelegramShareButton url={certificateUrl}>
                <TelegramIcon size={50} round />
              </TelegramShareButton>
              <EmailShareButton url={certificateUrl}>
                <EmailIcon size={50} round />
              </EmailShareButton>
            </div>

            <div className="share-link-container">
              <input
                type="text"
                value={certificateUrl}
                readOnly
                className="share-link"
              />
              <button onClick={handleCopy} className="copy-btn">
                {copied ? (
                  "Copied!"
                ) : (
                  <>
                    <LinkOutlined /> Copy Link
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </>
    );

    return isMobile ? (
      <Drawer
        title="Share"
        placement="bottom"
        onClose={handleClose}
        open={isModalOpen}
        className="share-certificates-modal"
      >
        {content}
      </Drawer>
    ) : (
      <Modal
        title="Share"
        open={isModalOpen}
        onCancel={handleClose}
        footer={null}
        className="share-certificates-modal"
      >
        {content}
      </Modal>
    );
  };

  return (
    <>
      <div className="account-stats-header">
        <div className="account-stats-header-Item">
          <p>Account</p>
          <Select
            value={options.find(
              (item) => Number(item.value) === Number(login_id)
            )}
            onChange={handleChange}
            options={options}
            isSearchable={false}
            styles={customStyles}
            className="react_select_container"
            classNamePrefix="react-select"
          />
          {statusCode !== undefined && (
            <div className={`status-btn ${colorClass}`}>{statusText}</div>
          )}
        </div>
        <div className="account-stats-header-Item metrics">
          <Link to="/trading-history" className="metrics-button">
            Trade History
          </Link>
          <Link to="/account-analysis" className="metrics-button">
            Analysis
          </Link>
          {showResetButton &&
            activeAccountFromList?.phase === "evaluation" &&
            activeAccountFromList?.status.toLowerCase() != "passed" && (
              <div
                className="metrics-button"
                onClick={() => onResetAccount && onResetAccount(data?.login_id)}
              >
                Reset
              </div>
            )}

          {data?.trading_platform === 1 ? (
            <a
              href="https://alphaticks.projectx.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="metrics-button"
            >
              Launch AlphaTicks
            </a>
          ) : (
            <a
              href="https://trader.tradovate.com/welcome"
              target="_blank"
              rel="noopener noreferrer"
              className="metrics-button"
            >
              Launch Tradovate/Ninja
            </a>
          )}
          <p className="metrics-button" onClick={handleOpenModal}>
            Share <img width={30} height={30} src={Share} alt="" />
          </p>
          <ShareCertificateModal
            data={data || null}
            isModalOpen={isModalOpen}
            handleClose={handleCloseModal}
          />
        </div>
      </div>
    </>
  );
};

export default AccountMetricsHeader;
